import { useState, useEffect } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { useStore } from '@nanostores/react';
import { chatStore } from '~/lib/stores/chat';
import { isMobile as checkIsMobile } from '~/utils/mobile';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateMobile = () => {
      setIsMobile(checkIsMobile());
    };
    
    updateMobile();
    window.addEventListener('resize', updateMobile);
    
    return () => window.removeEventListener('resize', updateMobile);
  }, []);

  return isMobile;
};

interface RibbonProps {
  isVisible: boolean;
  onClose?: () => void;
  onClaimClick?: () => void;
}

export function Ribbon2({ isVisible, onClose, onClaimClick }: RibbonProps) {
  const [ribbonClosing, setRibbonClosing] = useState(false);
  const chat = useStore(chatStore);
  const isMobile = useIsMobile();
  
  const handleCloseRibbon = () => {
    setRibbonClosing(true);
    setTimeout(() => {
      if (onClose) {
        onClose();
      }
    }, 350);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <style>
        {`
          @keyframes shine {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          
          .ribbon-shine {
            background: linear-gradient(
              -45deg,
              #000000,
              #1a1a1a,
              #000000,
              #2d2d2d
            );
            background-size: 400% 400%;
            animation: shine 6s ease infinite;
          }

          .sale-text {
            background: linear-gradient(to right, #bf953f, #fcf6ba, #b38728, #fbf5b7, #aa771c);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        `}
      </style>
      {!chat.started ? (
        <div
          className={`ribbon-shine${ribbonClosing ? ' closing' : ''}`}
          style={{
            color: '#fff',
            textAlign: 'center',
            fontSize: isMobile ? '0.75rem' : '0.95rem',
            minHeight: isMobile ? '40px' : '44px',
            height: 'auto',
            lineHeight: isMobile ? '1.3' : '44px',
            borderBottom: '1px solid #333',
            fontFamily: 'var(--alpha-font-family-body), sans-serif',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            position: 'relative',
            padding: isMobile ? '0.5rem 2rem 0.5rem 0.75rem' : '0 2.5rem 0 1rem',
            gap: isMobile ? '0.35rem' : '0',
            transition: 'opacity 0.35s cubic-bezier(0.4,0,0.2,1), transform 0.35s cubic-bezier(0.4,0,0.2,1)',
            opacity: ribbonClosing ? 0 : 1,
            transform: ribbonClosing ? 'translateY(-20px)' : 'translateY(0)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
            zIndex: 50
          }}
        >
          <div style={{ 
            display: isMobile ? 'flex' : 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? 'auto' : 'auto',
            gap: isMobile ? '4px' : '8px',
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            <span className="sale-text">BLACK FRIDAY</span>
            <span style={{ opacity: 0.9 }}>
              {isMobile ? '- 50% off premium plans!' : 'Get 50% off on all premium plans. Limited time offer!'}
            </span>
          </div>
          
          <button
            onClick={onClaimClick}
            style={{ 
              fontWeight: 600, 
              marginLeft: isMobile ? '0' : '1rem',
              color: '#fcf6ba',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              textDecoration: 'none',
              border: '1px solid #b38728',
              padding: isMobile ? '1px 8px' : '2px 12px',
              borderRadius: '12px',
              lineHeight: '1.4',
              fontSize: isMobile ? '0.75rem' : '0.85rem',
              background: 'rgba(179, 135, 40, 0.1)',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(179, 135, 40, 0.2)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(179, 135, 40, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isMobile ? 'Claim' : 'Claim Offer'}
            {!isMobile && <ChevronRight size={14} color="#fcf6ba" />}
          </button>
          
          <button
            onClick={handleCloseRibbon}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: isMobile ? '50%' : '50%',
              right: isMobile ? '0.25rem' : '1rem',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
          >
            <X size={18} />
          </button>
        </div>
      ) : (
        <></>
      )}
    </>
  );
}
